import { Globe, ArrowRight, ShieldCheck, Cpu } from "lucide-react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";

type Integration = {
  id: string;
  name: string;
  type: string;
  logo: string;
  status: "active" | "planned";
  description: string;
  link?: string;
};

const INTEGRATIONS_DATA: Integration[] = [
  {
    id: "okta-oin",
    name: "Okta Integration Network (OIN)",
    type: "Federated OIDC",
    logo: "/logo/okta.svg",
    status: "active",
    description: "Connect your tenant organizations seamlessly using Okta's enterprise OIDC integration protocol. Supports admin provisioning and user SSO flows.",
    link: "/docs/integrations/okta/oin"
  }
];

export const IntegrationsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="w-full min-h-screen bg-slate-50 dark:bg-slate-950 p-6 lg:p-12 selection:bg-orange-500 selection:text-white">
      <div className="mx-auto max-w-5xl">

        {/* Header */}
        <div className="mb-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 dark:bg-orange-500/10 text-orange-500 mb-4 shadow-sm"
          >
            <Globe className="w-6 h-6" />
          </motion.div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
            Identity Providers
          </p>
          <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 dark:text-white mt-2 tracking-tight">
            Supported Integrations
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-3 max-w-xl mx-auto">
            Connect Argus with enterprise Identity Providers to enable secure, federated Single Sign-On across all client applications.
          </p>
        </div>

        {/* Integration Catalog Grid */}
        <div className="grid gap-6 md:grid-cols-2">
          {INTEGRATIONS_DATA.map((item, index) => {
            const isActive = item.status === "active";
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05, duration: 0.4 }}
                onClick={() => isActive && item.link && navigate(item.link)}
                className={`group rounded-3xl border p-6 bg-white dark:bg-slate-900 text-left transition-all ${isActive
                  ? "border-slate-200 dark:border-slate-800 cursor-pointer hover:border-orange-500/40 dark:hover:border-orange-500/30 hover:shadow-md hover:-translate-y-0.5"
                  : "border-slate-100 dark:border-slate-850 opacity-60 pointer-events-none select-none"
                  }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 dark:bg-slate-950 p-2.5 border border-slate-100 dark:border-slate-800">
                    <img src={item.logo} alt={item.name} className="h-full object-contain" />
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${isActive
                    ? "bg-green-50 dark:bg-green-950/20 text-green-600 dark:text-green-400 border border-green-100 dark:border-green-900/30"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-400"
                    }`}>
                    {isActive ? "Available" : "Planned"}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-1.5 group-hover:text-orange-500 dark:group-hover:text-orange-400 transition-colors">
                  {item.name}
                </h3>
                <p className="text-xs font-semibold text-slate-400 mt-0.5">{item.type}</p>

                <p className="text-sm text-slate-500 dark:text-slate-400 mt-3 leading-relaxed">
                  {item.description}
                </p>

                {isActive && (
                  <div className="mt-6 flex items-center justify-end text-xs font-bold text-orange-500 group-hover:gap-1.5 transition-all">
                    <span>Configure Integration</span>
                    <ArrowRight className="w-3.5 h-3.5 ml-1 transition-transform group-hover:translate-x-1" />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Informational Cards */}
        <div className="grid gap-6 md:grid-cols-2 mt-12">
          <div className="bg-slate-100 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-850 rounded-3xl p-6 flex gap-4">
            <div className="bg-white dark:bg-slate-950 h-10 w-10 rounded-xl flex items-center justify-center shrink-0 text-slate-600 dark:text-slate-400 shadow-sm border border-slate-100 dark:border-slate-800">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-bold text-slate-900 dark:text-white text-sm">Security & Token Exchange</h4>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                Argus translates vendor JWTs (e.g. from Okta or Entra) into cryptographically signed, platform-trusted Argus tokens containing consolidated B2B role and permission sets.
              </p>
            </div>
          </div>

          <div className="bg-slate-100 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-850 rounded-3xl p-6 flex gap-4">
            <div className="bg-white dark:bg-slate-950 h-10 w-10 rounded-xl flex items-center justify-center shrink-0 text-slate-600 dark:text-slate-400 shadow-sm border border-slate-100 dark:border-slate-800">
              <Cpu className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-bold text-slate-900 dark:text-white text-sm">Zero Latency Evaluation</h4>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                Integrated client libraries validate tokens locally using cached public certificates, ensuring sub-10ms performance with zero database overhead.
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default IntegrationsPage;
